import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getIsMobileView, getIsTabView } from './Common.utils';

export function useOffClick(cb: Function, ref: React.MutableRefObject<HTMLDivElement>) {
	const windowClickListener = useCallback(
		(event: Event) => {
			const el = ref?.current as HTMLDivElement;
			const target = event.target as Node;
			if (!el || el.contains(target) || el === target) {
				return;
			}
			cb?.(event);
		},
		[cb, ref]
	);

	useEffect(() => {
		window.addEventListener('click', windowClickListener);
		return () => {
			window.removeEventListener('click', windowClickListener);
		};
	}, [windowClickListener]);
}

export function useIsMobileView(onResize?: Function) {
	const [isMobileView, setIsMobileView] = useState(false);

	const onResizeHandler = useCallback(() => {
		const newValue = getIsMobileView();
		setIsMobileView(newValue);
		if (onResize) onResize(newValue);
	}, [setIsMobileView, onResize]);

	useEffect(() => {
		window.addEventListener('resize', onResizeHandler);
		return () => {
			window.removeEventListener('resize', onResizeHandler);
		};
	}, [onResizeHandler]);

	return isMobileView;
}

export function useIsTabView(onResize?: Function, floId?: string) {
	const [isTabView, setIsTabView] = useState(getIsTabView(floId));

	const onResizeHandler = useCallback(() => {
		const newValue = getIsTabView(floId);
		setIsTabView(newValue);
		if (onResize) onResize(newValue);
	}, [setIsTabView, onResize]);

	useEffect(() => {
		window.addEventListener('resize', onResizeHandler);
		return () => {
			window.removeEventListener('resize', onResizeHandler);
		};
	}, [onResizeHandler]);

	return isTabView;
}

/**
 * Returns true when the document is hidden (tab in background, minimized, etc.)
 * and fires the optional callback whenever visibility changes.
 */
export function usePageVisibility(onChange?: (isHidden: boolean) => void) {
	const [isHidden, setIsHidden] = useState<boolean>(() =>
		typeof document !== 'undefined' ? document.hidden : false
	);

	useEffect(() => {
		if (typeof document === 'undefined') return;

		const handleVisibilityChange = () => {
			const hidden = document.hidden;
			setIsHidden(hidden);
			onChange?.(hidden);
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [onChange]);

	return isHidden;
}

interface VisibilityLanguageAudio {
	play: (videoTimeMs?: number) => void;
	pauseAudioOnly: () => void;
	isAudioPaused?: () => boolean;
}

export function useMediaVisibilityPause(
	containerRef: React.RefObject<HTMLElement>,
	languageAudioRef: React.MutableRefObject<VisibilityLanguageAudio>,
	onLanguageAudioPlayStateChange: (isPlaying: boolean) => void,
	onVisibilityLanguagePauseChange?: (isPaused: boolean) => void
) {
	const pausedMediaRef = useRef<Set<HTMLMediaElement>>(new Set());
	const resumeLanguageAudioRef = useRef(false);

	const handleLanguageAudioPlayStateChange = useCallback(
		(isPlaying: boolean) => {
			onLanguageAudioPlayStateChange(isPlaying);
			if (isPlaying && typeof document !== 'undefined' && document.hidden) {
				resumeLanguageAudioRef.current = true;
				onVisibilityLanguagePauseChange?.(true);
				languageAudioRef.current.pauseAudioOnly();
			}
		},
		[languageAudioRef, onLanguageAudioPlayStateChange, onVisibilityLanguagePauseChange]
	);

	useEffect(() => {
		if (typeof document === 'undefined') return;

		const pauseMedia = (media: HTMLMediaElement) => {
			if (!media.paused) {
				pausedMediaRef.current.add(media);
				media.pause();
			}
		};
		const handlePlay = (event: Event) => {
			const media = event.target;
			if (
				document.hidden &&
				media instanceof HTMLMediaElement &&
				containerRef.current?.contains(media)
			) {
				pauseMedia(media);
			}
		};
		const handleVisibilityChange = () => {
			if (document.hidden) {
				containerRef.current
					?.querySelectorAll<HTMLMediaElement>('video, audio')
					.forEach(pauseMedia);
				if (languageAudioRef.current.isAudioPaused?.() === false) {
					resumeLanguageAudioRef.current = true;
					onVisibilityLanguagePauseChange?.(true);
					languageAudioRef.current.pauseAudioOnly();
				}
				return;
			}

			const mediaToResume = Array.from(pausedMediaRef.current);
			pausedMediaRef.current.clear();
			mediaToResume.forEach((media) => media.play().catch(() => {}));
			if (resumeLanguageAudioRef.current) {
				resumeLanguageAudioRef.current = false;
				onVisibilityLanguagePauseChange?.(false);
				languageAudioRef.current.play();
			}
		};

		document.addEventListener('play', handlePlay, true);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		handleVisibilityChange();
		return () => {
			document.removeEventListener('play', handlePlay, true);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			pausedMediaRef.current.clear();
			resumeLanguageAudioRef.current = false;
		};
	}, [containerRef, languageAudioRef, onVisibilityLanguagePauseChange]);

	return handleLanguageAudioPlayStateChange;
}
